import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderKanban, Upload, Plus, Pencil, LogOut, ChevronRight,
  ArrowLeft, User as UserIcon, Gauge, UserCheck, CheckCircle2, Lock, Save,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/FloralBg";

const TABS = ["Overview", "Users", "Groups"];

export default function AdminDash() {
  const [tab, setTab] = useState("Overview");
  const { logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen">
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between border-b border-border/60 glass-strong sticky top-0 z-30">
        <div className="flex items-center gap-3"><Logo /><span className="pill text-[11px] px-3 py-1 bg-[#00D4D4]/15 text-teal-accent">ADMIN</span></div>
        <button data-testid="logout" onClick={() => { logout(); nav("/login"); }} className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300"><LogOut size={16} /> Logout</button>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="glass rounded-full p-1.5 inline-flex gap-1 mb-8">
          {TABS.map((t) => (
            <button key={t} data-testid={`admin-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
                    className={`pill px-5 py-2 text-sm ${tab === t ? "bg-[#00D4D4]/15 text-teal-accent" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>
          ))}
        </div>
        {tab === "Overview" && <Overview />}
        {tab === "Users" && <UsersTab />}
        {tab === "Groups" && <GroupsTab />}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- shared */
function BackBtn({ onClick, label = "Back" }) {
  return (
    <button data-testid="back-btn" onClick={onClick} className="flex items-center gap-2 text-sm text-teal-accent hover:text-white mb-5 transition-colors">
      <ArrowLeft size={16} /> {label}
    </button>
  );
}
function StatCard({ icon: Icon, value, label, testid, onClick, accent }) {
  const clickable = !!onClick;
  return (
    <button disabled={!clickable} onClick={onClick} data-testid={testid}
            className={`glass ${clickable ? "glass-hover cursor-pointer" : "cursor-default"} rounded-3xl p-6 text-left w-full ${accent ? "border-[#00D4D4]/40" : ""}`}>
      <Icon size={22} className="text-teal-accent mb-4" />
      <div className="serif text-4xl font-extrabold">{value}</div>
      <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">{label} {clickable && <ChevronRight size={13} />}</div>
    </button>
  );
}
function EnrollTable({ rows, status }) {
  const grid = "md:grid-cols-[2fr_1.2fr_1.1fr_1.5fr_1.5fr_1fr]";
  return (
    <div className="glass-strong rounded-2xl overflow-hidden">
      <div className={`hidden md:grid ${grid} gap-3 px-5 py-3 text-[11px] uppercase tracking-widest text-muted-foreground border-b border-border/60`}>
        <span>Mail ID</span><span>Admin</span><span>Group</span><span>Course</span><span>Start (IST)</span>
        <span>{status === "complete" ? "Completed (IST)" : "Progress"}</span>
      </div>
      <div className="divide-y divide-border/40">
        {rows.map((r, i) => (
          <div key={i} data-testid={`enroll-row-${i}`} className={`grid ${grid} gap-1 md:gap-3 px-5 py-3 text-sm`}>
            <span className="font-medium truncate">{r.email}</span>
            <span className="text-muted-foreground">{r.admin_name}</span>
            <span className="text-muted-foreground">{r.group_name}</span>
            <span className="text-muted-foreground truncate" title={r.course_title}>{r.course_title}</span>
            <span className="text-muted-foreground text-xs">{r.start_date}</span>
            {status === "complete"
              ? <span className="text-teal-accent text-xs">{r.end_date || "—"}</span>
              : <span className="text-teal-accent font-medium">{r.progress}</span>}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground px-5 py-6">No records.</p>}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Overview */
function Overview() {
  const [screen, setScreen] = useState("cards"); // cards | profile | byGroup | list | groupsList | groupUsers
  const [ov, setOv] = useState(null);
  const [ctx, setCtx] = useState({});

  const load = () => api.get("/admin/overview").then((r) => setOv(r.data));
  useEffect(() => { load(); }, []);

  const openByGroup = async (status) => {
    const { data } = await api.get(`/admin/enroll-summary?status=${status}`);
    setCtx({ status, groups: data }); setScreen("byGroup");
  };
  const openList = async (grp) => {
    const { data } = await api.get(`/admin/enroll-list?status=${ctx.status}&group_id=${grp.group_id}`);
    setCtx({ ...ctx, rows: data, groupName: grp.group_name }); setScreen("list");
  };
  const openGroupsList = async () => {
    const { data } = await api.get("/admin/groups");
    setCtx({ allGroups: data }); setScreen("groupsList");
  };
  const openGroupUsers = async (grp) => {
    const { data } = await api.get(`/admin/groups/${grp.id}/users`);
    setCtx((c) => ({ ...c, groupName: grp.name, userRows: data })); setScreen("groupUsers");
  };

  if (screen === "profile") return <Profile ov={ov} onBack={() => setScreen("cards")} />;

  if (screen === "groupsList") return (
    <div>
      <BackBtn onClick={() => setScreen("cards")} label="Back to overview" />
      <h2 className="serif text-2xl font-bold mb-1">Groups</h2>
      <p className="text-sm text-muted-foreground mb-5">{(ctx.allGroups || []).length} group{(ctx.allGroups || []).length !== 1 ? "s" : ""}</p>
      <div className="space-y-2 max-w-2xl">
        {(ctx.allGroups || []).map((g) => (
          <button key={g.id} data-testid={`group-card-${g.id}`} onClick={() => openGroupUsers(g)}
                  className="w-full glass glass-hover rounded-xl px-5 py-4 flex items-center justify-between text-left">
            <div><div className="font-medium">{g.name}</div><div className="text-xs text-muted-foreground">{g.user_count} users</div></div>
            <ChevronRight size={16} className="text-muted-foreground" />
          </button>
        ))}
        {(ctx.allGroups || []).length === 0 && <p className="text-sm text-muted-foreground">No groups yet.</p>}
      </div>
    </div>
  );

  if (screen === "groupUsers") return (
    <div>
      <BackBtn onClick={() => setScreen("groupsList")} label="Back to groups" />
      <h2 className="serif text-2xl font-bold mb-1">{ctx.groupName}</h2>
      <p className="text-sm text-muted-foreground mb-5">{(ctx.userRows || []).length} record{(ctx.userRows || []).length !== 1 ? "s" : ""}</p>
      <GroupUsersTable rows={ctx.userRows || []} />
    </div>
  );

  if (screen === "byGroup") return (
    <div>
      <BackBtn onClick={() => setScreen("cards")} label="Back to overview" />
      <h2 className="serif text-2xl font-bold mb-1">{ctx.status === "complete" ? "Complete" : "Active"} enrollments</h2>
      <p className="text-sm text-muted-foreground mb-5">Grouped by group</p>
      <div className="space-y-2 max-w-2xl">
        {ctx.groups.map((g) => (
          <button key={g.group_id || "none"} data-testid={`bygroup-${g.group_id}`} onClick={() => openList(g)}
                  className="w-full glass glass-hover rounded-xl px-5 py-4 flex items-center justify-between text-left">
            <span className="font-medium">{g.group_name}</span>
            <span className="flex items-center gap-3 text-sm text-muted-foreground"><span className="text-teal-accent font-medium">{g.count}</span> students <ChevronRight size={16} /></span>
          </button>
        ))}
        {ctx.groups.length === 0 && <p className="text-sm text-muted-foreground">No {ctx.status} enrollments.</p>}
      </div>
    </div>
  );

  if (screen === "list") return (
    <div>
      <BackBtn onClick={() => setScreen("byGroup")} label="Back to groups" />
      <h2 className="serif text-2xl font-bold mb-1">{ctx.groupName}</h2>
      <p className="text-sm text-muted-foreground mb-5">{ctx.status === "complete" ? "Complete" : "Active"} · {ctx.rows.length} students</p>
      <EnrollTable rows={ctx.rows} status={ctx.status} />
    </div>
  );

  if (!ov) return <div className="text-teal-accent">Loading…</div>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      <StatCard icon={UserIcon} value="Profile" label="View & change password" testid="ov-profile" onClick={() => setScreen("profile")} accent />
      <StatCard icon={Gauge} value={ov.max_users} label="Total Limit" testid="ov-total-limit" />
      <StatCard icon={Gauge} value={ov.remaining} label="Remaining Limit" testid="ov-remaining-limit" />
      <StatCard icon={UserCheck} value={ov.active_count} label="Active Enrollment" testid="ov-active" onClick={() => openByGroup("active")} accent />
      <StatCard icon={CheckCircle2} value={ov.complete_count} label="Complete Enrollment" testid="ov-complete" onClick={() => openByGroup("complete")} accent />
      <StatCard icon={FolderKanban} value={ov.group_count} label="Groups" testid="ov-groups" onClick={openGroupsList} accent />
    </div>
  );
}

function GroupUsersTable({ rows }) {
  const grid = "md:grid-cols-[2fr_1.2fr_1.6fr_1.5fr_1.5fr_1.5fr]";
  return (
    <div className="glass-strong rounded-2xl overflow-hidden">
      <div className={`hidden md:grid ${grid} gap-3 px-5 py-3 text-[11px] uppercase tracking-widest text-muted-foreground border-b border-border/60`}>
        <span>Mail ID</span><span>Group Name</span><span>Generated Time</span><span>Course Name</span><span>Course Start Time</span><span>Complete Time</span>
      </div>
      <div className="divide-y divide-border/40">
        {rows.map((r, i) => (
          <div key={i} data-testid={`groupuser-row-${i}`} className={`grid ${grid} gap-1 md:gap-3 px-5 py-3 text-sm`}>
            <span className="font-medium truncate">{r.email}</span>
            <span className="text-muted-foreground">{r.group_name}</span>
            <span className="text-muted-foreground text-xs">{r.generated_time}</span>
            <span className="text-muted-foreground truncate" title={r.course_title}>{r.course_name}</span>
            <span className="text-muted-foreground text-xs">{r.course_start}</span>
            <span className={r.complete_time === "-" ? "text-muted-foreground text-xs" : "text-teal-accent text-xs"}>{r.complete_time}</span>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground px-5 py-6">No users in this group.</p>}
      </div>
    </div>
  );
}

function Profile({ ov, onBack }) {
  const [cur, setCur] = useState(""); const [np, setNp] = useState(""); const [cp, setCp] = useState("");
  const [busy, setBusy] = useState(false);
  const update = async () => {
    if (!cur || !np) return toast.error("Fill all password fields");
    if (np !== cp) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: np });
      toast.success("Password updated"); setCur(""); setNp(""); setCp("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-xl">
      <BackBtn onClick={onBack} label="Back to overview" />
      <div className="glass-strong rounded-3xl p-7 mb-5">
        <h2 className="serif text-2xl font-bold mb-5">Profile</h2>
        <ReadRow label="Admin Name" value={ov?.name} />
        <ReadRow label="Email / Mail ID" value={ov?.email} />
      </div>
      <div className="glass-strong rounded-3xl p-7">
        <h3 className="serif text-lg font-bold mb-4">Change password</h3>
        <PwInp label="Current Password" value={cur} onChange={setCur} testid="cur-password" />
        <PwInp label="New Password" value={np} onChange={setNp} testid="new-password" />
        <PwInp label="Confirm New Password" value={cp} onChange={setCp} testid="confirm-password" />
        <button data-testid="update-password-btn" onClick={update} disabled={busy} className="kiq-btn w-full py-2.5 text-sm mt-2 flex items-center justify-center gap-2"><Save size={15} /> {busy ? "Updating…" : "Update Password"}</button>
      </div>
    </div>
  );
}
function ReadRow({ label, value }) {
  return (
    <div className="glass rounded-xl px-4 py-3 mb-3 flex items-center justify-between">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
function PwInp({ label, value, onChange, testid }) {
  return (
    <div className="mb-4">
      <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{label}</label>
      <div className="kiq-input flex items-center gap-2 px-4 py-2.5">
        <Lock size={15} className="text-teal-accent" />
        <input data-testid={testid} type="password" value={value} onChange={(e) => onChange(e.target.value)} className="bg-transparent outline-none w-full text-sm" />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Users */
function UsersTab() {
  const [groups, setGroups] = useState([]);
  const [file, setFile] = useState(null);
  const [uploadGroup, setUploadGroup] = useState("");
  useEffect(() => { api.get("/admin/groups").then((r) => setGroups(r.data)); }, []);
  const upload = async () => {
    if (!file) return toast.error("Choose a CSV/Excel file (Column1=Email, Column2=Password)");
    const fd = new FormData(); fd.append("file", file); if (uploadGroup) fd.append("group_id", uploadGroup);
    try { const { data } = await api.post("/admin/users/bulk-upload", fd); toast.success(`Created ${data.created} users${data.skipped.length ? `, ${data.skipped.length} skipped` : ""}`); setFile(null); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-xl">
      <div className="glass-strong rounded-3xl p-6">
        <h3 className="serif text-xl font-bold mb-1">Bulk create users</h3>
        <p className="text-xs text-muted-foreground mb-4">Upload CSV/Excel — Column 1 = Email, Column 2 = Password.</p>
        <label className="block glass rounded-2xl border-dashed border-[#00D4D4]/30 p-5 text-center cursor-pointer hover:border-[#00D4D4]/60 mb-4">
          <Upload size={22} className="text-teal-accent mx-auto mb-2" />
          <span className="text-sm">{file ? file.name : "Choose file"}</span>
          <input data-testid="bulk-file" type="file" className="hidden" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} />
        </label>
        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Assign to group</label>
        <select data-testid="upload-group" value={uploadGroup} onChange={(e) => setUploadGroup(e.target.value)} className="kiq-input w-full px-3 py-2.5 text-sm mb-4">
          <option value="">— No group —</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <button data-testid="upload-users-btn" onClick={upload} className="kiq-btn w-full py-2.5 text-sm">Upload &amp; create</button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Groups */
function GroupsTab() {
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState("");
  const [rename, setRename] = useState(null);
  const load = () => api.get("/admin/groups").then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);
  const createGroup = async () => {
    if (!newGroup.trim()) return;
    try { await api.post("/admin/groups", { name: newGroup }); toast.success("Group created"); setNewGroup(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doRename = async () => { await api.put(`/admin/groups/${rename.id}`, { name: rename.name }); toast.success("Renamed"); setRename(null); load(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="glass-strong rounded-3xl p-6">
        <h3 className="serif text-xl font-bold mb-4">Create group</h3>
        <input data-testid="group-name" value={newGroup} onChange={(e) => setNewGroup(e.target.value)} placeholder="e.g. Batch 2026 / CSE-A" className="kiq-input w-full px-4 py-2.5 text-sm mb-4" />
        <button data-testid="create-group-btn" onClick={createGroup} className="kiq-btn w-full py-2.5 text-sm flex items-center justify-center gap-2"><Plus size={15} /> Create group</button>
      </div>
      <div className="lg:col-span-2 glass-strong rounded-3xl p-6">
        <h3 className="serif text-xl font-bold mb-4 flex items-center gap-2"><FolderKanban size={18} className="text-teal-accent" /> Groups ({groups.length})</h3>
        <div className="space-y-2">
          {groups.map((g) => (
            <div key={g.id} className="glass rounded-xl px-4 py-3 flex items-center justify-between">
              <div><div className="font-medium">{g.name}</div><div className="text-xs text-muted-foreground">{g.user_count} users</div></div>
              <div className="flex gap-1">
                <button data-testid={`rename-group-${g.id}`} onClick={() => setRename({ id: g.id, name: g.name })} className="p-2 rounded-lg hover:bg-white/5" title="Rename"><Pencil size={15} /></button>
              </div>
            </div>
          ))}
          {groups.length === 0 && <p className="text-sm text-muted-foreground">No groups yet.</p>}
        </div>
      </div>

      {rename && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={() => setRename(null)}>
          <div className="glass-strong rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="serif text-xl mb-4">Rename group</h3>
            <input data-testid="rename-group-input" value={rename.name} onChange={(e) => setRename({ ...rename, name: e.target.value })} className="kiq-input w-full px-4 py-3 text-sm mb-4" autoFocus />
            <div className="flex gap-3"><button onClick={() => setRename(null)} className="flex-1 py-2.5 rounded-full glass text-sm">Cancel</button><button data-testid="rename-group-save" onClick={doRename} className="flex-1 kiq-btn py-2.5 text-sm">Save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
